import { IProductCard } from '@src/components/product-card/product-card.types'
import collectionService from '@src/pages/[...slug]/components/collection/services/collectionService'
import { getProductCardsFromCollection } from '@src/pages/[...slug]/components/collection/services/collectionService/utils'
import { Gender } from '@src/quince-site-common/src/domains/Gender'
import Product from '@src/quince-site-common/src/domains/Product'
import Bundle, { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import productRecommendationService from '@src/quince-site-common/src/services/productRecommendationService'

export const getSelectedColor = (selectedOptions: ProductVariantOption[]) =>
  selectedOptions.filter(o => o.name == ProductOptionName.Color)[0]?.value

export interface IGetRecommendedProductsApiParams {
  product: Product | Bundle
  isNewPDPRecommendations: boolean
  selectedOptions: ProductVariantOption[]
  recommendedProductsType: RECOMMENDED_PRODUCTS_TYPE
  zipCode?: string
}

export enum RECOMMENDED_PRODUCTS_TYPE {
  MTE = 'MTE',
  CTL = 'CTL',
  QSAL = 'QSAL',
  BOYT = 'BOYT',
}

export const RECOMMENDATION_TYPE_TO_COLLECTION_MAP = {
  [RECOMMENDED_PRODUCTS_TYPE.MTE]: 'mte',
  [RECOMMENDED_PRODUCTS_TYPE.CTL]: 'ctl',
  [RECOMMENDED_PRODUCTS_TYPE.BOYT]: 'boyt',
  [RECOMMENDED_PRODUCTS_TYPE.QSAL]: 'qsal',
} as const

export const RECOMMENDATION_TYPE_TO_PRODUCT_LIMIT_MAP = {
  [RECOMMENDED_PRODUCTS_TYPE.CTL]: 20,
  [RECOMMENDED_PRODUCTS_TYPE.MTE]: 40,
  [RECOMMENDED_PRODUCTS_TYPE.BOYT]: 20,
  [RECOMMENDED_PRODUCTS_TYPE.QSAL]: 40,
} as const

export const getProductShopifyIdForRecommendations = (
  product: Product | Bundle,
  selectedOptions: ProductVariantOption[],
) => {
  if (product.type === PRODUCT_TYPES.BUNDLE) {
    return (
      product.customBundleProducts?.length &&
      productHelper.getShopifyProductId(product.customBundleProducts[0] as unknown as Product, selectedOptions)
    )
  }
  return productHelper.getShopifyProductId(product, selectedOptions)
}

export const getProductIdForRecommendations = (product: Product | Bundle) => {
  if (product.type === PRODUCT_TYPES.BUNDLE) {
    return getFirstProductIdForBundle(product)
  }
  return product.id
}

export const getFirstProductIdForBundle = (bundle: Bundle): number | undefined => {
  if (!bundle.customBundleProducts?.length) return undefined
  const firstProduct = bundle.customBundleProducts[0]
  if (!firstProduct) return undefined
  return Number(firstProduct.id)
}

export const getCollectionIdForRecommendations = (
  product: Product | Bundle,
  recommendedProductsType: RECOMMENDED_PRODUCTS_TYPE,
) => {
  const collectionKey = RECOMMENDATION_TYPE_TO_COLLECTION_MAP[recommendedProductsType]
  const rec = product.productRecommendations?.[collectionKey]
  return rec?.data ? rec.data.contentful_id : ''
}

export const getRecommendedProductsApi = async ({
  product,
  isNewPDPRecommendations,
  selectedOptions,
  recommendedProductsType,
  zipCode,
}: IGetRecommendedProductsApiParams): Promise<IProductCard[]> => {
  const shopifyProductId = getProductShopifyIdForRecommendations(product, selectedOptions)
  const selectedColor = getSelectedColor(selectedOptions)

  if (!selectedColor) {
    return []
  }

  const limit = RECOMMENDATION_TYPE_TO_PRODUCT_LIMIT_MAP[recommendedProductsType]
  if (isNewPDPRecommendations) {
    const collectionId = getCollectionIdForRecommendations(product, recommendedProductsType)
    const collection = await collectionService.getCollectionById({
      collectionId: collectionId,
      limit,
      productIdForRecommendations: getProductIdForRecommendations(product),
      colorForProductRecommendations: selectedColor,
    })

    return getProductCardsFromCollection(collection)
  } else {
    /**
     * Remove this whole else block once experiment is concluded
     * TODO: STRFRNT-8674
     */
    if (recommendedProductsType === RECOMMENDED_PRODUCTS_TYPE.MTE) {
      return productRecommendationService.getSimilarProducts(
        shopifyProductId as number,
        selectedColor,
        {
          viewContextGender: product.gender,
          limit: 40,
        },
        true,
      )
    }

    if (recommendedProductsType === RECOMMENDED_PRODUCTS_TYPE.CTL) {
      const productLimit = 6
      return productRecommendationService.getPairWithProducts(
        shopifyProductId as number,
        selectedOptions,
        product.gender as Gender,
        true,
        zipCode,
        productLimit,
      )
    }

    if (recommendedProductsType === RECOMMENDED_PRODUCTS_TYPE.QSAL) {
      return productRecommendationService.getFBTProducts(shopifyProductId as number, selectedColor, {
        viewContextGender: product.gender,
        useLite: true,
        limit,
        zipcode: zipCode,
      })
    }
  }

  return []
}
